import { useEffect, useRef } from 'react'
import { i18n } from '@lingui/core'
import { useLocale } from './I18nProvider'
import { DEFAULT_LOCALE } from './locales'

type CatalogLoader = (locale: string) => Promise<{ messages: Record<string, string> }>

/**
 * Hook that lazily loads a screen's translation catalog when it mounts.
 *
 * Usage in a screen component:
 *
 *   import { useScreenMessages } from '@data/i18n/useScreenMessages'
 *
 *   const loadCatalog: CatalogLoader = (locale) =>
 *     import(`./locales/${locale}.po`)
 *
 *   const MyScreen = () => {
 *     useScreenMessages(loadCatalog)
 *     return <Text><Trans>Hello world</Trans></Text>
 *   }
 *
 * The catalog is loaded once per locale change. English strings are compiled
 * inline by the Lingui Babel macro, so no network request is made for English.
 */
export const useScreenMessages = (loadCatalog: CatalogLoader) => {
  const { locale } = useLocale()
  const loadedRef = useRef<string | null>(null)
  // Store loadCatalog in a ref so callers don't need to memoize it
  const loaderRef = useRef(loadCatalog)
  loaderRef.current = loadCatalog

  useEffect(() => {
    if (locale === DEFAULT_LOCALE) return
    if (loadedRef.current === locale) return

    let cancelled = false

    const load = async () => {
      try {
        const catalog = await loaderRef.current(locale)
        if (cancelled) return
        let messages = catalog.messages

        // Regional variants inherit from their base locale — merge base
        // catalog with regional overrides so untranslated strings fall back
        // to the base language, not English.
        const BASE_LOCALE: Record<string, string> = { 'fr-CA': 'fr', 'pt-BR': 'pt' }
        const base = BASE_LOCALE[locale]
        if (base) {
          try {
            const baseCatalog = await loaderRef.current(base)
            messages = { ...baseCatalog.messages, ...messages }
          } catch {}
        }

        i18n.load(locale, messages)
        i18n.activate(locale)
        loadedRef.current = locale
      } catch (err) {
        // Catalog missing for this screen/locale — fall back to English
        if (__DEV__) {
          console.warn(`[i18n] Failed to load catalog for locale "${locale}":`, err)
        }
      }
    }

    load()

    return () => {
      cancelled = true
    }
  }, [locale])
}
